/**
 * The rule engine: a pure function from (previous engine state, sample,
 * thresholds) to the issues that are active, the events that just happened and
 * the recovery actions that are due. It owns hysteresis and the recovery state
 * machine but performs no I/O; the monitor executes the requested actions and
 * feeds their outcomes back in with the next sample.
 *
 * Time inside the engine is the sum of the measured sample intervals, so the
 * engine needs no clock of its own and scripted tests control it completely.
 */
import type {
  HealthEvent,
  HealthIssue,
  HealthIssueCode,
  HealthSample,
  HealthState,
  HealthThresholds,
  PeerHealth,
} from './api';

/** Peer id used for issues about the WHIP publish connection. */
export const WHIP_PEER_ID = 'whip';

export type RecoveryAction = 'resumeAudio' | 'restartRenderLoop' | 'reconnectPeer';

export interface RecoveryPolicy {
  action: RecoveryAction;
  /** Wait after the issue is raised before the first attempt. */
  initialDelayMs: number;
  /** Wait after attempt n before the next one or the verdict: backoffMs * 2^(n-1). */
  backoffMs: number;
  /** Quick attempts before the issue is marked failed and retries drop to the slow cadence. */
  maxAttempts: number;
}

export const RECOVERY_POLICIES: Partial<Record<HealthIssueCode, RecoveryPolicy>> = {
  'audio-suspended': { action: 'resumeAudio', initialDelayMs: 0, backoffMs: 2000, maxAttempts: 3 },
  'render-stall': { action: 'restartRenderLoop', initialDelayMs: 0, backoffMs: 2000, maxAttempts: 3 },
  'render-context-lost': { action: 'restartRenderLoop', initialDelayMs: 0, backoffMs: 2000, maxAttempts: 3 },
  'receiver-frozen': { action: 'reconnectPeer', initialDelayMs: 2000, backoffMs: 5000, maxAttempts: 3 },
  'no-outbound-video': { action: 'reconnectPeer', initialDelayMs: 2000, backoffMs: 5000, maxAttempts: 3 },
  // The network layer runs its own ICE restart first; only step in once that has had its chance.
  'peer-disconnected': { action: 'reconnectPeer', initialDelayMs: 8000, backoffMs: 10000, maxAttempts: 3 },
};

export const HEALTH_TUNING = {
  /** Consecutive good samples before a raised issue clears. */
  clearSamples: 2,
  starvedClearSamples: 5,
  noOutboundHoldMs: 2000,
  disconnectedHoldMs: 2000,
  outputDropRatio: 0.1,
  outputDropHoldMs: 3000,
  /** Frames queued in the swarm encoder beyond this count as falling behind. */
  encoderQueueLimit: 6,
  encoderOverloadHoldMs: 5000,
  /** Composed frames reaching nobody for this long is a stall even though the compositor is running. */
  undeliveredHoldMs: 2000,
  /** Share of the target frame rate lost to a late main thread before it is worth reporting. */
  starvedFrameRatio: 0.1,
  mainThreadStarvedHoldMs: 3000,
  /** Allowed deviation of the audio clock rate from 1. */
  audioClockTolerance: 0.15,
  audioStallHoldMs: 2000,
  /** Master peak above this counts as "we are sending signal". */
  signalPeakDb: -50,
  receiverSilentHoldMs: 3000,
  /**
   * A receiver's lip-sync estimate starts a few hundred ms off and takes 10-20 s to converge, so
   * it is not judged until the peer has been reporting for this long.
   */
  receiverAvSyncSettleMs: 30000,
  receiverAvSyncHoldMs: 5000,
  /**
   * A receiver lip-sync reading beyond this is a broken playout-timestamp mapping (seen at 20-30 s
   * after audio concealment), not a lip-sync error: it is ignored and restarts the settling time.
   */
  receiverAvSyncPlausibleMs: 3000,
  /** Share of concealed receiver audio in a report interval that counts as an audio disturbance. */
  receiverAudioDisturbedRatio: 0.25,
  degradedHoldMs: 5000,
  degradedLossPct: 5,
  degradedRttMs: 500,
  /** A receiver report older than this no longer describes the present. */
  reportFreshMs: 2500,
  feedbackLostMs: 10000,
  recordingStallMs: 5000,
  statsStaleHoldMs: 5000,
  /** A/V clock skew is the change in wall-minus-audio time across this window. */
  skewWindowMs: 30000,
  /**
   * Once the quick attempts are used up the issue is marked failed, but a stream must not stay
   * broken because a budget ran out: recovery keeps being tried at this fixed, slow cadence for as
   * long as the issue persists.
   */
  slowRetryMs: 15000,
  /** A recovery action that has not reported back after this long counts as a failed attempt. */
  recoveryTimeoutMs: 15000,
  /** A re-raise within this time of clearing continues the old attempt count instead of starting over. */
  recoveryMemoryMs: 60000,
} as const;

export interface RecoveryRequest {
  issueId: string;
  code: HealthIssueCode;
  peerId?: string;
  action: RecoveryAction;
  /** 1-based. */
  attempt: number;
}

export interface RecoveryResult {
  issueId: string;
  attempt: number;
  ok: boolean;
  error?: string;
}

export interface EvaluationContext {
  /** Whether anything is live; when false the result is `idle` with no issues. */
  live: boolean;
  /** The P2P stats source has stopped answering; per-peer values in the sample are old. */
  peerStatsStale?: boolean;
  whipStatsStale?: boolean;
  /** Peers (WHIP as WHIP_PEER_ID) whose video encoder is known to run in software. */
  softwareEncoderPeers?: readonly string[];
  /**
   * Peers whose latest receiver report shows audio disturbed (heavy concealment, or packets not
   * advancing); their lip-sync estimate has to converge again afterwards.
   */
  audioDisturbedPeers?: readonly string[];
  /** Outcomes of recovery actions requested by earlier evaluations. */
  recoveryResults?: readonly RecoveryResult[];
  /** Longest time a clock tick waited on the main thread during this interval. */
  tickLagMs?: number;
}

type RecoveryPhase = 'idle' | 'running' | 'settling';

export interface ConditionState {
  code: HealthIssueCode;
  peerId?: string;
  /** Engine-clock time the condition was first seen bad in its current run; null while good. */
  badSinceMs: number | null;
  /** Wall-clock time matching badSinceMs. */
  sinceAt: number;
  goodSamples: number;
  raised: boolean;
  severity: HealthIssue['severity'];
  message: string;
  /** Engine-clock time of the last clear, for recovery memory. */
  clearedAtMs: number | null;
  attempts: number;
  /** The quick attempts did not fix it; retries continue at the slow cadence. */
  exhausted: boolean;
  /** Attempt count when the issue was last raised, to tell whether this episode saw an attempt. */
  attemptsAtRaise: number;
  phase: RecoveryPhase;
  attemptStartedMs: number;
  nextAttemptMs: number;
  lastOutcome: string | null;
}

export interface HealthEngineState {
  /** Sum of measured sample intervals. */
  clockMs: number;
  live: boolean;
  conditions: Readonly<Record<string, ConditionState>>;
  /** Engine-clock time each peer's receiver reports were first seen. */
  reportingSince: Readonly<Record<string, number>>;
  /** The previous sample spanned a starved interval. */
  lastStarved: boolean;
}

export interface HealthEvaluation {
  state: HealthEngineState;
  status: HealthState['status'];
  issues: HealthIssue[];
  events: HealthEvent[];
  actions: RecoveryRequest[];
  /** The monitor should start its A/V skew window over at the next sample. */
  resetAvSkew: boolean;
}

export function createHealthEngineState(): HealthEngineState {
  return { clockMs: 0, live: false, conditions: {}, reportingSince: {}, lastStarved: false };
}

export function issueId(code: HealthIssueCode, peerId?: string): string {
  return peerId === undefined ? code : `${code}:${peerId}`;
}

export function formatDuration(ms: number): string {
  const seconds = Math.max(0, ms) / 1000;
  if (seconds < 10) return `${seconds.toFixed(1)} s`;
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ${Math.round(seconds - minutes * 60)} s`;
}

/**
 * " for 2.4 s", or nothing while the condition is younger than a second. Issues raised on first
 * sight are announced with their raise-time text, where a duration would always read "0.0 s".
 */
function lasting(forMs: number): string {
  return forMs < 1000 ? '' : ` for ${formatDuration(forMs)}`;
}

type Verdict = 'bad' | 'good' | 'unknown';

interface Finding {
  code: HealthIssueCode;
  peerId?: string;
  verdict: Verdict;
  /** How long the condition must stay bad before the issue is raised. */
  holdMs: number;
  clearSamples: number;
  severity: HealthIssue['severity'];
  /** Wall-clock start of the condition when the sample itself knows it (e.g. from a frame age). */
  sinceAt?: number;
  /** Built only for bad verdicts; `forMs` is how long the condition has lasted. */
  message: (forMs: number) => string;
}

interface Frame {
  sample: HealthSample;
  thresholds: HealthThresholds;
  /** Rates in this sample were measured over a trustworthy interval. */
  ratesKnown: boolean;
  /**
   * Audio counters reach the page through main-thread messages, so the first sample after a
   * starved interval may still be built on values that queued up behind the stall.
   */
  audioRatesKnown: boolean;
  starved: boolean;
  wentLive: boolean;
  /** No frame has been composed for longer than the stall threshold. */
  stalled: boolean;
  /** Frames are composed but none is accepted by the program track. */
  undelivered: boolean;
  /** We are composing and delivering frames at a usable rate. */
  producing: boolean;
  tickLagMs: number;
}

function finding(
  code: HealthIssueCode,
  verdict: Verdict,
  severity: HealthIssue['severity'],
  message: (forMs: number) => string,
  options: { holdMs?: number; clearSamples?: number; sinceAt?: number; peerId?: string } = {},
): Finding {
  return {
    code,
    verdict,
    severity,
    message,
    holdMs: options.holdMs ?? 0,
    clearSamples: options.clearSamples ?? HEALTH_TUNING.clearSamples,
    sinceAt: options.sinceAt,
    peerId: options.peerId,
  };
}

function videoFindings(f: Frame, out: Finding[]): void {
  const { sample, thresholds } = f;
  const v = sample.video;

  out.push(
    finding(
      'render-context-lost',
      v.contextLost ? 'bad' : 'good',
      'critical',
      (forMs) =>
        `The GPU context is lost${lasting(forMs)}; nothing can be composed and viewers see a frozen picture. ` +
        'Restoring it is attempted automatically. If this does not clear, reload the studio tab.',
    ),
  );

  out.push(
    finding(
      'render-stall',
      f.stalled || f.undelivered ? 'bad' : 'good',
      'critical',
      (forMs) =>
        (f.stalled
          ? `No video frame has been composed for ${formatDuration(v.lastFrameAgeMs)}; `
          : `Frames are composed but none has reached the program output for ${formatDuration(forMs)}; `) +
        'viewers and the recording see a frozen picture. ' +
        'The compositor is restarted automatically. If this does not clear, bring the studio tab to the front or reload it.',
      f.stalled ? { sinceAt: sample.at - v.lastFrameAgeMs } : { holdMs: HEALTH_TUNING.undeliveredHoldMs },
    ),
  );

  const starvedLimit = Math.max(1, v.targetFps * HEALTH_TUNING.starvedFrameRatio);
  out.push(
    finding(
      'main-thread-starved',
      !f.ratesKnown ? 'unknown' : v.starvedFps > starvedLimit ? 'bad' : 'good',
      'warning',
      (forMs) =>
        `${v.starvedFps.toFixed(1)} frames per second have been skipped for ${formatDuration(forMs)} because the page's main thread ` +
        'is not being run in time (hidden or throttled tab, or other heavy work on the page). ' +
        'Keep the studio visible in its own window and close heavy tabs.',
      { holdMs: HEALTH_TUNING.mainThreadStarvedHoldMs },
    ),
  );

  const fpsKnown = f.ratesKnown && !f.stalled && v.targetFps > 0;
  out.push(
    finding(
      'low-fps',
      !fpsKnown ? 'unknown' : v.producedFps < v.targetFps * thresholds.lowFpsRatio ? 'bad' : 'good',
      'warning',
      (forMs) =>
        `Video is being composed at ${v.producedFps.toFixed(1)} fps instead of ${v.targetFps} fps for ${formatDuration(forMs)}` +
        (v.failedFps > 0 ? ` (${v.failedFps.toFixed(1)} frames per second fail to compose). ` : '. ') +
        'The machine cannot keep up: lower the output resolution or frame rate, or remove heavy sources and filters.',
      { holdMs: thresholds.lowFpsSeconds * 1000 },
    ),
  );

  const dropRatio = v.producedFps > 0 ? (v.producedFps - v.deliveredFps) / v.producedFps : 0;
  out.push(
    finding(
      'output-dropping',
      // Delivering nothing at all is a stall, reported as one.
      !f.ratesKnown || f.stalled || f.undelivered || v.producedFps <= 0
        ? 'unknown'
        : dropRatio > HEALTH_TUNING.outputDropRatio
          ? 'bad'
          : 'good',
      'warning',
      (forMs) =>
        `The program output has been rejecting ${Math.round(dropRatio * 100)}% of composed frames for ${formatDuration(forMs)} ` +
        `(${v.deliveredFps.toFixed(1)} of ${v.producedFps.toFixed(1)} fps delivered). ` +
        'An encoder or recorder is not keeping up: lower the bitrate, resolution or frame rate.',
      { holdMs: HEALTH_TUNING.outputDropHoldMs },
    ),
  );

  const enc = sample.encoder;
  out.push(
    finding(
      'encoder-overloaded',
      !enc ? 'good' : !f.ratesKnown ? 'unknown' : enc.droppedDelta > 0 || enc.queue > HEALTH_TUNING.encoderQueueLimit ? 'bad' : 'good',
      'warning',
      (forMs) =>
        `The swarm video encoder has been ${enc && enc.droppedDelta > 0 ? 'dropping frames' : `holding ${enc?.queue ?? 0} frames back`} ` +
        `for ${formatDuration(forMs)}; viewers see stutter and the stream falls behind. ` +
        'Lower the output resolution or frame rate, or close other heavy work on this computer. Keep the bitrate unless upload is also limited.',
      { holdMs: HEALTH_TUNING.encoderOverloadHoldMs },
    ),
  );

  out.push(
    finding(
      'video-track-ended',
      v.trackState === 'live' ? 'good' : 'bad',
      'critical',
      (forMs) =>
        (v.trackState === 'missing'
          ? `There is no program video track${lasting(forMs)}; `
          : `The program video track has ended${lasting(forMs)}; `) +
        'nothing is being sent or recorded. Stop and restart the output, or reload the studio.',
    ),
  );
}

function audioFindings(f: Frame, out: Finding[]): void {
  const { sample } = f;
  const a = sample.audio;
  const running = a.contextState === 'running';

  out.push(
    finding(
      'audio-suspended',
      running ? 'good' : 'bad',
      'critical',
      (forMs) =>
        (a.contextState === 'missing'
          ? `There is no audio engine${lasting(forMs)}; `
          : `The audio engine is ${a.contextState}${lasting(forMs)}; `) +
        'the stream has no sound. Resuming is attempted automatically; if it does not clear, click anywhere in the studio window.',
    ),
  );

  const clockOff = Math.abs(a.clockRate - 1) > HEALTH_TUNING.audioClockTolerance;
  const notPulled = a.quantaPerSecond < 1;
  out.push(
    finding(
      'audio-stall',
      !running ? 'good' : !f.audioRatesKnown ? 'unknown' : clockOff || notPulled ? 'bad' : 'good',
      'critical',
      (forMs) =>
        (notPulled
          ? `The audio engine reports running but has processed no audio for ${formatDuration(forMs)}. `
          : `The audio clock has been running at ${Math.round(a.clockRate * 100)}% of real time for ${formatDuration(forMs)}. `) +
        'Sound on the stream is missing or breaking up. Check the output audio device, then reload the studio if it persists.',
      { holdMs: HEALTH_TUNING.audioStallHoldMs },
    ),
  );

  out.push(
    finding(
      'audio-track-ended',
      a.trackState === 'live' ? 'good' : 'bad',
      'critical',
      (forMs) =>
        (a.trackState === 'missing'
          ? `There is no program audio track${lasting(forMs)}; `
          : `The program audio track has ended${lasting(forMs)}; `) +
        'the stream has no sound. Stop and restart the output, or reload the studio.',
    ),
  );

  const skew = sample.avSkewMs;
  out.push(
    finding(
      'av-skew',
      f.wentLive ? 'unknown' : Math.abs(skew) > f.thresholds.avSkewMs ? 'bad' : 'good',
      'warning',
      () =>
        `Within the last ${formatDuration(HEALTH_TUNING.skewWindowMs)} the audio clock ${skew > 0 ? 'fell' : 'ran'} ${Math.round(Math.abs(skew))} ms ` +
        `${skew > 0 ? 'behind' : 'ahead of'} the wall clock that video frames are stamped from. ` +
        'Audio skipped or stalled, or the audio device is running at the wrong rate; if this keeps returning, check the audio device.',
    ),
  );
}

function globalFindings(f: Frame, context: EvaluationContext, out: Finding[]): void {
  const { sample } = f;
  const rec = sample.recording;

  out.push(
    finding(
      'recording-stalled',
      !rec || !rec.active
        ? 'good'
        : f.stalled || f.undelivered
          ? 'bad'
          : !f.ratesKnown
            ? 'unknown'
            : rec.bytesPerSecond <= 0
              ? 'bad'
              : 'good',
      'critical',
      (forMs) =>
        f.stalled || f.undelivered
          ? `The recording has been capturing a frozen picture for ${formatDuration(forMs)} because no new video frames reach it. ` +
            'It continues by itself once video resumes; the frozen stretch stays in the file.'
          : `The recording has written no data for ${formatDuration(forMs)}. ` +
            'Check free disk space, then stop and restart the recording; what was written so far is kept.',
      { holdMs: HEALTH_TUNING.recordingStallMs },
    ),
  );

  const cause =
    f.tickLagMs > sample.intervalMs / 2
      ? "the page's main thread was blocked"
      : sample.visibility.state === 'hidden' || sample.visibility.wasFrozen
        ? 'the page was throttled or frozen in the background'
        : 'its timer was delayed';
  out.push(
    finding(
      'monitor-starved',
      f.starved ? 'bad' : 'good',
      'warning',
      () =>
        `The health monitor could not run for ${formatDuration(sample.intervalMs)} (${cause}); the stream may have frozen during that time. ` +
        'Keep the studio in a visible tab or its own window and close heavy pages.',
      { clearSamples: HEALTH_TUNING.starvedClearSamples },
    ),
  );

  out.push(
    finding(
      'page-hidden',
      sample.visibility.state === 'hidden' ? 'bad' : 'good',
      'info',
      () =>
        `The studio tab is hidden${lasting(sample.visibility.hiddenForMs)} while live. ` +
        'Browsers slow down hidden tabs; keep it visible or in its own window for a stable stream.',
      { clearSamples: 1 },
    ),
  );

  out.push(
    finding(
      'peer-degraded',
      context.peerStatsStale ? 'bad' : 'good',
      'warning',
      (forMs) =>
        `Connection statistics have been unavailable for ${formatDuration(forMs)}; viewer health cannot be verified. ` +
        'If viewers report problems, restart the broadcast.',
      { holdMs: HEALTH_TUNING.statsStaleHoldMs },
    ),
  );
}

function peerFindings(
  f: Frame,
  p: PeerHealth,
  isWhip: boolean,
  stale: boolean,
  reportingForMs: number,
  softwareEncoder: boolean,
  out: Finding[],
): void {
  const { sample, thresholds } = f;
  const peerId = isWhip ? WHIP_PEER_ID : p.peerId;
  const who = isWhip ? 'The WHIP connection' : `Viewer ${p.peerId}`;
  const connected = p.connectionState === 'connected';
  const lost =
    p.connectionState === 'failed' || p.connectionState === 'closed' || p.connectionState === 'disconnected';
  const hasReport = p.reportAgeMs !== null;
  const reportFresh = hasReport && (p.reportAgeMs as number) <= HEALTH_TUNING.reportFreshMs;
  const feedbackLost = hasReport && (p.reportAgeMs as number) > HEALTH_TUNING.feedbackLostMs;
  const notSending = p.sentFps !== null && p.sentFps < 1;
  const whenStale = (verdict: Verdict): Verdict => (stale ? 'unknown' : verdict);

  out.push(
    finding(
      'peer-disconnected',
      whenStale(lost ? 'bad' : 'good'),
      'critical',
      (forMs) =>
        `${who} is ${p.connectionState}${lasting(forMs)} and receives nothing. ` +
        (isWhip
          ? 'The WHIP publisher reconnects on its own, so the watchdog does not intervene; check the network and the WHIP endpoint if it does not come back.'
          : 'A reconnect is attempted automatically; check the network if it does not come back.'),
      { peerId, holdMs: p.connectionState === 'disconnected' ? HEALTH_TUNING.disconnectedHoldMs : 0 },
    ),
  );

  out.push(
    finding(
      'no-outbound-video',
      whenStale(
        !connected ? 'good' : !f.ratesKnown || p.sentFps === null ? 'unknown' : !notSending ? 'good' : f.producing ? 'bad' : 'unknown',
      ),
      'critical',
      (forMs) =>
        `${who} is connected but has been sent no video for ${formatDuration(forMs)} although the program is being composed ` +
        `at ${sample.video.producedFps.toFixed(1)} fps. ` +
        (isWhip
          ? 'The WHIP publisher reconnects on its own, so the watchdog does not intervene; stop and restart the WHIP output if it does not recover.'
          : 'The connection is rebuilt automatically.'),
      { peerId, holdMs: HEALTH_TUNING.noOutboundHoldMs },
    ),
  );

  const age = p.receiverFrameAgeMs;
  let frozen: Verdict;
  if (!connected || !hasReport || age === null || feedbackLost) frozen = 'good';
  else if (!reportFresh) frozen = 'unknown';
  else if (age <= thresholds.receiverFrozenMs) frozen = 'good';
  // Our own stall or an empty sender explains the receiver's freeze; those have their own issues.
  else if (!f.producing || notSending) frozen = 'unknown';
  else frozen = 'bad';
  out.push(
    finding(
      'receiver-frozen',
      whenStale(frozen),
      'critical',
      () =>
        `${who} has not decoded a new video frame for ${formatDuration(age ?? 0)} while frames are being sent` +
        (p.sentFps !== null ? ` at ${p.sentFps.toFixed(1)} fps` : '') +
        '; their picture is frozen (network or decoder). The connection is rebuilt automatically.',
      { peerId, sinceAt: age === null ? undefined : sample.at - age },
    ),
  );

  let silent: Verdict;
  if (!connected || !hasReport || feedbackLost) silent = 'good';
  else if (p.audioFlowing === null) silent = 'unknown';
  else if (p.audioFlowing) silent = 'good';
  // A silent master bus makes a silent receiver correct; we cannot tell whether audio would get through.
  else silent = sample.audio.masterPeakDb > HEALTH_TUNING.signalPeakDb ? 'bad' : 'unknown';
  out.push(
    finding(
      'receiver-silent',
      whenStale(silent),
      'critical',
      (forMs) =>
        `${who} has received no audio for ${formatDuration(forMs)} although the master bus peaks at ` +
        `${sample.audio.masterPeakDb.toFixed(0)} dBFS. Ask them to reload; restart the broadcast if several viewers are affected.`,
      { peerId, holdMs: HEALTH_TUNING.receiverSilentHoldMs },
    ),
  );

  const sync = p.receiverAvSyncMs;
  out.push(
    finding(
      'receiver-av-sync',
      whenStale(
        // A stale report, or one taken while no audio was playing, says nothing about lip sync now;
        // treating it as unknown would keep an old error on screen.
        !connected ||
          !hasReport ||
          !reportFresh ||
          p.audioFlowing !== true ||
          sample.audio.contextState !== 'running' ||
          reportingForMs < HEALTH_TUNING.receiverAvSyncSettleMs ||
          !plausibleSync(sync)
          ? 'good'
          : Math.abs(sync as number) > thresholds.receiverAvSyncMs
              ? 'bad'
              : 'good',
      ),
      'warning',
      (forMs) =>
        `${who} has measured ${sync !== null && sync > 0 ? 'video' : 'audio'} ${Math.round(Math.abs(sync ?? 0))} ms late ` +
        `for ${formatDuration(forMs)} (lip-sync error). It usually settles; if not, ask them to reload.`,
      { peerId, holdMs: HEALTH_TUNING.receiverAvSyncHoldMs },
    ),
  );

  const reasons: string[] = [];
  if (connected) {
    if (p.packetLossPct !== null && p.packetLossPct > HEALTH_TUNING.degradedLossPct) {
      reasons.push(`${p.packetLossPct.toFixed(1)}% packet loss`);
    }
    if (p.rttMs !== null && p.rttMs > HEALTH_TUNING.degradedRttMs) reasons.push(`${Math.round(p.rttMs)} ms round trip`);
    if (p.qualityLimitationReason === 'cpu') {
      reasons.push(softwareEncoder ? 'software video encoder limited by CPU' : 'encoder limited by CPU');
    }
    if (p.qualityLimitationReason === 'bandwidth') reasons.push('encoder limited by bandwidth');
    if (feedbackLost) {
      reasons.push(`receiver feedback was lost ${formatDuration(p.reportAgeMs ?? 0)} ago, so their playback cannot be verified`);
    }
  }
  out.push(
    finding(
      'peer-degraded',
      isWhip && stale ? 'bad' : whenStale(reasons.length > 0 ? 'bad' : 'good'),
      'warning',
      (forMs) =>
        isWhip && stale
          ? `WHIP connection statistics have been unavailable for ${formatDuration(forMs)}; its health cannot be verified.`
          : `${who} has had a degraded connection for ${formatDuration(forMs)}: ${reasons.join(', ')}. ` +
            (softwareEncoder && p.qualityLimitationReason === 'cpu'
              ? 'Video is being encoded in software: switch the codec to H.264 so the hardware encoder can be used, or lower the resolution or frame rate.'
              : 'Lower the video bitrate or resolution if this persists.'),
      { peerId, holdMs: isWhip && stale ? HEALTH_TUNING.statsStaleHoldMs : HEALTH_TUNING.degradedHoldMs },
    ),
  );
}

/** A lip-sync reading that can describe a real offset; anything else is not a measurement. */
function plausibleSync(sync: number | null): boolean {
  return sync !== null && Math.abs(sync) <= HEALTH_TUNING.receiverAvSyncPlausibleMs;
}

function newCondition(f: Finding): ConditionState {
  return {
    code: f.code,
    peerId: f.peerId,
    badSinceMs: null,
    sinceAt: 0,
    goodSamples: 0,
    raised: false,
    severity: f.severity,
    message: '',
    clearedAtMs: null,
    attempts: 0,
    exhausted: false,
    attemptsAtRaise: 0,
    phase: 'idle',
    attemptStartedMs: 0,
    nextAttemptMs: 0,
    lastOutcome: null,
  };
}

/**
 * What to tell the operator when an issue clears. The raised message describes a fault and what to
 * do about it, which is wrong to repeat once the fault is gone.
 */
export function resolvedText(code: HealthIssueCode, peerId?: string): string {
  const who = peerId === WHIP_PEER_ID ? 'The WHIP connection' : `Viewer ${peerId ?? ''}`;
  switch (code) {
    case 'render-stall':
      return 'Video frames are reaching the program output again.';
    case 'render-context-lost':
      return 'The GPU context was restored.';
    case 'main-thread-starved':
      return 'Frames are no longer being skipped for lack of main-thread time.';
    case 'low-fps':
      return 'Video is being composed at the target frame rate again.';
    case 'output-dropping':
      return 'The program output is accepting every frame again.';
    case 'encoder-overloaded':
      return 'The swarm video encoder is keeping up again.';
    case 'video-track-ended':
      return 'The program video track is live again.';
    case 'audio-suspended':
      return 'The audio engine resumed.';
    case 'audio-stall':
      return 'The audio engine is processing audio in real time again.';
    case 'audio-track-ended':
      return 'The program audio track is live again.';
    case 'av-skew':
      return 'The audio and video clocks are back in step.';
    case 'no-outbound-video':
      return `${who} is being sent video again.`;
    case 'receiver-frozen':
      return `${who} is decoding new video frames again.`;
    case 'receiver-silent':
      return `${who} is receiving audio again.`;
    case 'receiver-av-sync':
      return `${who} no longer reports a lip-sync error.`;
    case 'peer-disconnected':
      return `${who} is connected again.`;
    case 'peer-degraded':
      return peerId === undefined ? 'Connection statistics are available again.' : `${who} no longer has a degraded connection.`;
    case 'recording-stalled':
      return 'The recording is receiving data and live video again.';
    case 'monitor-starved':
      return 'The health monitor is running on time again.';
    case 'page-hidden':
      return 'The studio tab is visible again.';
  }
}

const ACTION_TEXT: Record<RecoveryAction, string> = {
  resumeAudio: 'resuming the audio engine',
  restartRenderLoop: 'restarting the render loop',
  reconnectPeer: 'reconnecting the peer',
};

const SEVERITY_RANK: Record<HealthIssue['severity'], number> = { info: 0, warning: 1, critical: 2 };
const STALL_CODES: readonly HealthIssueCode[] = ['render-stall', 'audio-suspended', 'audio-stall'];
const AUDIO_STALL_CODES: readonly HealthIssueCode[] = ['audio-suspended', 'audio-stall'];

function recoveryOf(c: ConditionState): HealthIssue['recovery'] {
  if (c.attempts === 0) return 'none';
  if (c.goodSamples > 0) return 'recovered';
  return c.exhausted ? 'failed' : 'attempting';
}

export function evaluateHealth(
  prev: HealthEngineState,
  sample: HealthSample,
  thresholds: HealthThresholds,
  context: EvaluationContext = { live: true },
): HealthEvaluation {
  const clockMs = prev.clockMs + Math.max(0, sample.intervalMs);
  const events: HealthEvent[] = [];
  const event = (type: HealthEvent['type'], c: ConditionState, detail: string): void => {
    events.push(
      c.peerId === undefined
        ? { at: sample.at, type, code: c.code, detail }
        : { at: sample.at, type, code: c.code, peerId: c.peerId, detail },
    );
  };

  if (!context.live) {
    for (const c of Object.values(prev.conditions)) {
      if (c.raised) event('issue-cleared', c, 'Nothing is live any more.');
    }
    return {
      state: { ...createHealthEngineState(), clockMs },
      status: 'idle',
      issues: [],
      events,
      actions: [],
      resetAvSkew: false,
    };
  }

  const wentLive = !prev.live;
  // The sample that starts a live session spans idle time, so its rates and interval say nothing about the session.
  const starved = !wentLive && sample.intervalMs > thresholds.starvedIntervalMs;
  const ratesKnown = !wentLive && !starved && sample.intervalMs > 0;
  const stalled = sample.video.lastFrameAgeMs > thresholds.renderStallMs;
  const undelivered = ratesKnown && !stalled && sample.video.producedFps > 0 && sample.video.deliveredFps <= 0;
  const frame: Frame = {
    sample,
    thresholds,
    ratesKnown,
    audioRatesKnown: ratesKnown && !prev.lastStarved,
    starved,
    wentLive,
    stalled,
    undelivered,
    producing:
      !stalled &&
      !undelivered &&
      (!ratesKnown || sample.video.producedFps >= Math.max(1, sample.video.targetFps * 0.5)),
    tickLagMs: context.tickLagMs ?? 0,
  };

  const findings: Finding[] = [];
  videoFindings(frame, findings);
  audioFindings(frame, findings);
  globalFindings(frame, context, findings);
  const reportingSince: Record<string, number> = {};
  const disturbed = context.audioDisturbedPeers ?? [];
  const reportingFor = (peer: PeerHealth, id: string): number => {
    if (peer.reportAgeMs === null) return 0;
    // The receiver's synchroniser converges again from scratch whenever its audio was interrupted.
    const interrupted =
      sample.audio.contextState !== 'running' ||
      peer.audioFlowing !== true ||
      disturbed.includes(id) ||
      (peer.receiverAvSyncMs !== null && !plausibleSync(peer.receiverAvSyncMs));
    reportingSince[id] = interrupted ? clockMs : (prev.reportingSince[id] ?? clockMs);
    return clockMs - reportingSince[id];
  };
  const software = context.softwareEncoderPeers ?? [];
  for (const peer of sample.peers) {
    peerFindings(
      frame,
      peer,
      false,
      context.peerStatsStale === true,
      reportingFor(peer, peer.peerId),
      software.includes(peer.peerId),
      findings,
    );
  }
  if (sample.whip) {
    peerFindings(
      frame,
      sample.whip,
      true,
      context.whipStatsStale === true,
      reportingFor(sample.whip, WHIP_PEER_ID),
      software.includes(WHIP_PEER_ID),
      findings,
    );
  }

  const results = new Map<string, RecoveryResult>();
  for (const r of context.recoveryResults ?? []) results.set(r.issueId, r);

  const conditions: Record<string, ConditionState> = {};
  const actions: RecoveryRequest[] = [];
  let stallCleared = false;
  let audioStallCleared = false;

  for (const f of findings) {
    const id = issueId(f.code, f.peerId);
    const before = prev.conditions[id];
    const c: ConditionState = before ? { ...before } : newCondition(f);

    if (f.verdict === 'bad') {
      c.goodSamples = 0;
      if (c.badSinceMs === null) {
        c.badSinceMs = clockMs;
        c.sinceAt = f.sinceAt ?? sample.at;
      }
      c.severity = f.severity;
      c.message = f.message(Math.max(0, sample.at - c.sinceAt));
      if (!c.raised && clockMs - c.badSinceMs >= f.holdMs) {
        c.raised = true;
        if (c.clearedAtMs === null || clockMs - c.clearedAtMs > HEALTH_TUNING.recoveryMemoryMs) {
          c.attempts = 0;
          c.exhausted = false;
          c.lastOutcome = null;
        }
        c.phase = 'idle';
        c.attemptsAtRaise = c.attempts;
        c.nextAttemptMs = clockMs + (RECOVERY_POLICIES[c.code]?.initialDelayMs ?? 0);
        // A flapping issue that already used its quick attempts stays on the slow cadence.
        if (c.exhausted) c.nextAttemptMs = Math.max(c.nextAttemptMs, c.attemptStartedMs + HEALTH_TUNING.slowRetryMs);
        event('issue-raised', c, c.message);
      }
    } else if (f.verdict === 'good') {
      if (c.raised) {
        c.goodSamples++;
        if (c.goodSamples >= f.clearSamples) {
          if (c.attempts > c.attemptsAtRaise) {
            const made = c.attempts - c.attemptsAtRaise;
            event(
              'recovery-succeeded',
              c,
              `Cleared after ${made} recovery attempt${made === 1 ? '' : 's'}.`,
            );
          }
          event(
            'issue-cleared',
            c,
            `${resolvedText(c.code, c.peerId)} The issue lasted ${formatDuration(sample.at - c.sinceAt)}.`,
          );
          if (STALL_CODES.includes(c.code)) stallCleared = true;
          if (AUDIO_STALL_CODES.includes(c.code)) audioStallCleared = true;
          c.raised = false;
          c.badSinceMs = null;
          c.goodSamples = 0;
          c.clearedAtMs = clockMs;
          c.phase = 'idle';
        }
      } else {
        c.badSinceMs = null;
      }
    }

    const policy = RECOVERY_POLICIES[c.code];
    // WHIP reconnects itself and there is no recovery hook for it.
    // A lost context also stalls rendering; one compositor recovery at a time is enough.
    const deferred = c.code === 'render-stall' && conditions[issueId('render-context-lost')]?.raised === true;
    if (c.raised && policy && c.peerId !== WHIP_PEER_ID && !deferred) {
      const slowNextMs = c.attemptStartedMs + HEALTH_TUNING.slowRetryMs;
      if (c.phase === 'running') {
        const result = results.get(id);
        if (result && result.attempt === c.attempts) {
          c.lastOutcome = result.ok ? 'reported success' : (result.error ?? 'reported no effect');
          c.phase = 'settling';
          c.nextAttemptMs = c.exhausted ? slowNextMs : clockMs + policy.backoffMs * 2 ** (c.attempts - 1);
        } else if (clockMs - c.attemptStartedMs >= HEALTH_TUNING.recoveryTimeoutMs) {
          c.lastOutcome = 'did not finish in time';
          c.phase = 'settling';
          c.nextAttemptMs = c.exhausted ? Math.max(clockMs, slowNextMs) : clockMs;
        }
      }
      if ((c.phase === 'idle' || c.phase === 'settling') && f.verdict === 'bad' && clockMs >= c.nextAttemptMs) {
        if (!c.exhausted && c.attempts >= policy.maxAttempts) {
          c.exhausted = true;
          c.nextAttemptMs = Math.max(clockMs, slowNextMs);
          event(
            'recovery-failed',
            c,
            `Still present after ${c.attempts} recovery attempts (last attempt ${c.lastOutcome ?? 'had no effect'}). ` +
              `Manual action is needed; recovery keeps being retried every ${formatDuration(HEALTH_TUNING.slowRetryMs)}.`,
          );
        }
        if (clockMs >= c.nextAttemptMs) {
          c.attempts++;
          c.phase = 'running';
          c.attemptStartedMs = clockMs;
          const request: RecoveryRequest = { issueId: id, code: c.code, action: policy.action, attempt: c.attempts };
          if (c.peerId !== undefined) request.peerId = c.peerId;
          actions.push(request);
          event(
            'recovery-attempted',
            c,
            (c.exhausted
              ? `Retry ${c.attempts - policy.maxAttempts} after the first ${policy.maxAttempts} attempts failed: `
              : `Attempt ${c.attempts} of ${policy.maxAttempts}: `) +
              ACTION_TEXT[policy.action] +
              (c.lastOutcome ? ` (previous attempt ${c.lastOutcome}).` : '.'),
          );
        }
      }
    }

    const remembered =
      c.attempts > 0 && c.clearedAtMs !== null && clockMs - c.clearedAtMs <= HEALTH_TUNING.recoveryMemoryMs;
    if (c.raised || c.badSinceMs !== null || remembered) conditions[id] = c;
  }

  // Conditions with no finding this time belong to peers that are gone.
  for (const [id, c] of Object.entries(prev.conditions)) {
    if (c.raised && !(id in conditions) && !findings.some((f) => issueId(f.code, f.peerId) === id)) {
      event('issue-cleared', c, 'The peer is no longer connected to the studio.');
    }
  }

  // After an audio interruption every receiver's synchroniser has to converge again.
  if (audioStallCleared) {
    for (const id of Object.keys(reportingSince)) reportingSince[id] = clockMs;
  }
  const resetAvSkew = wentLive || stallCleared;

  const issues: HealthIssue[] = [];
  let worst = -1;
  for (const [id, c] of Object.entries(conditions)) {
    if (!c.raised) continue;
    const issue: HealthIssue = {
      id,
      code: c.code,
      severity: c.severity,
      since: c.sinceAt,
      message: c.message,
      recovery: recoveryOf(c),
    };
    if (c.peerId !== undefined) issue.peerId = c.peerId;
    issues.push(issue);
    worst = Math.max(worst, SEVERITY_RANK[c.severity]);
  }
  issues.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || a.since - b.since);

  return {
    state: { clockMs, live: true, conditions, reportingSince, lastStarved: starved },
    status: worst >= 2 ? 'critical' : worst === 1 ? 'warning' : 'healthy',
    issues,
    events,
    actions,
    resetAvSkew,
  };
}
